import type { PlanungUnterlage } from './types';

/**
 * Der Schreiber der Unterlagen-Checkliste — ohne React, damit die Regeln
 * gegen den Wettlauf ohne jsdom pruefbar sind (`unterlagenSpeicher.test.ts`).
 *
 * Die ganze Checkliste ist EINE jsonb-Spalte; jeder Schreibvorgang schickt
 * den vollstaendigen Stand. Daraus folgen vier Regeln:
 *
 *   1. Hoechstens EIN Schreibvorgang unterwegs. Zwei gleichzeitige koennten
 *      in vertauschter Reihenfolge ankommen, dann stuende der aeltere Stand
 *      in der Datenbank — still, und niemand merkte es.
 *   2. Der juengste Stand gewinnt — JE PLANUNGSPHASE. Kommt waehrend eines
 *      Laufs etwas hinzu, schreibt derselbe Lauf danach den juengsten Stand;
 *      Zwischenstaende derselben Phase werden uebersprungen, weil der
 *      juengere sie enthaelt.
 *   3. Ein Fehler verwirft nichts. Der gescheiterte Stand bleibt vorgemerkt
 *      (sofern nicht schon ein juengerer DERSELBEN Phase wartet) und geht bei
 *      jedem weiteren Anstoss erneut raus.
 *   4. Phasen verdraengen einander nie. Die Auftraege liegen je `phaseId`:
 *      ein gescheiterter Stand von Phase A wird von einer Aenderung an
 *      Phase B weder ersetzt noch blockiert, und die Fehlermeldung bleibt
 *      stehen, bis auch A gespeichert ist — der Erfolg von B sagt ueber A
 *      nichts. (Frueher genuegte ein einziger Platz: nach einem Wechsel in
 *      derselben Modulinstanz ersetzte die erste Aenderung an B den
 *      wartenden Stand von A, und A war verloren, ohne dass es jemand sah.)
 */

export type Speicherstand = 'gespeichert' | 'wartet' | 'speichert' | 'fehler';

export interface SpeicherAuftrag {
    phaseId: string;
    liste: PlanungUnterlage[];
}

export interface UnterlagenSchreiber {
    /**
     * Stand vormerken; geschrieben wird nach `verzoegerungMs` (0 = sofort).
     * Liefert die VERSION des Auftrags — je Phase fortlaufend, Grundlage fuer
     * `bestaetigung`.
     */
    vormerken: (auftrag: SpeicherAuftrag, verzoegerungMs: number) => number;
    /** Jetzt schreiben, was aussteht („Erneut speichern"). */
    speichern: () => Promise<void>;
    /** Beim Verlassen: Entprelltes sofort schreiben statt verfallen lassen. */
    abschliessen: () => void;
    /**
     * Der juengste noch nicht bestaetigte Stand einer Phase — vorgemerkt,
     * gescheitert oder gerade unterwegs — oder `null`. Nur lesend.
     */
    ausstehendFuer: (phaseId: string) => PlanungUnterlage[] | null;
    /** Die zuletzt vergebene Version einer Phase (0, wenn nie etwas vorgemerkt wurde). */
    aktuelleVersion: (phaseId: string) => number;
    /**
     * Zusage, dass der Stand mit dieser Version in der Datenbank steht.
     *
     * Erfuellt, sobald ein Schreibvorgang der Phase mit Version >= `version`
     * bestaetigt ist — ein juengerer Stand enthaelt die aeltere Aenderung
     * (Regel 2); ist das schon geschehen, sofort. Abgelehnt, wenn der Auftrag
     * dieser Version (bzw. der juengere, der ihn traegt) scheitert und kein
     * noch juengerer Stand derselben Phase mehr wartet. Der Erfolg eines
     * AELTEREN Schreibvorgangs sagt nichts: laeuft A und wird waehrenddessen
     * B eingereiht, bestaetigt A's Erfolg nur A — B wartet auf B.
     * Fuer Stellen, die NICHT „eingereiht", sondern „gespeichert" melden
     * muessen — der Stempel nach einem Versand.
     */
    bestaetigung: (phaseId: string, version: number) => Promise<void>;
}

/**
 * Die Liste, die beim (Rueck-)Wechsel zu einer Phase sichtbar wird und auf
 * der die naechste Aenderung aufbaut.
 *
 * Liegt fuer die Phase noch ein unbestaetigter Stand im Schreiber, gewinnt
 * ER — nicht der Datenbankstand aus `phase.unterlagen`. Sonst gaelte:
 * Aenderung an A scheitert, Wechsel nach B, zurueck nach A zeigt den alten
 * Datenbankstand, und die naechste Aenderung an A ersetzte den wartenden
 * Auftrag durch eine Liste OHNE die erste Aenderung — still verloren.
 */
export const sichtbareListe = (
    phase: { id: string; unterlagen: PlanungUnterlage[] } | null,
    schreiber: Pick<UnterlagenSchreiber, 'ausstehendFuer'>,
): PlanungUnterlage[] => {
    if (!phase) return [];
    return schreiber.ausstehendFuer(phase.id) || phase.unterlagen || [];
};

/** Ein vorgemerkter Stand samt seiner Version je Phase. */
interface Eintrag {
    auftrag: SpeicherAuftrag;
    version: number;
}

interface Warter {
    version: number;
    erfuellen: () => void;
    ablehnen: (e: unknown) => void;
}

export const erzeugeUnterlagenSchreiber = (opt: {
    schreibe: (auftrag: SpeicherAuftrag) => Promise<unknown>;
    /** `fehler` undefined = Meldung unveraendert lassen. */
    melde: (stand: Speicherstand, fehler?: string | null) => void;
    fehlerText: (e: unknown) => string;
}): UnterlagenSchreiber => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    /** Je Planungsphase der juengste noch nicht geschriebene Stand. */
    const ausstehend = new Map<string, Eintrag>();
    /** Der Auftrag, der gerade geschrieben wird — er ist aus `ausstehend`
     *  schon heraus, aber noch nicht bestaetigt. */
    let unterwegs: Eintrag | null = null;
    let laeuft = false;
    /** Je Phase die zuletzt vergebene Version. */
    const letzteVersion = new Map<string, number>();
    /** Je Phase die hoechste bestaetigt geschriebene Version. */
    const bestaetigtBis = new Map<string, number>();
    /** Wer je Phase auf die Datenbankbestaetigung einer Version wartet. */
    const warter = new Map<string, Warter[]>();

    /** Nimmt die Wartenden heraus, deren Version der Auftrag `version` traegt. */
    const warterBis = (phaseId: string, version: number): Warter[] => {
        const alle = warter.get(phaseId) || [];
        const betroffen = alle.filter(w => w.version <= version);
        const rest = alle.filter(w => w.version > version);
        if (rest.length) warter.set(phaseId, rest); else warter.delete(phaseId);
        return betroffen;
    };

    const speichern = async (): Promise<void> => {
        // Der laufende Durchgang nimmt neue Staende nach seinem Ende mit.
        if (laeuft) return;
        laeuft = true;
        // In DIESEM Durchgang gescheiterte Auftraege — sie werden nicht sofort
        // wiederholt (das liefe bei einem dauerhaften Fehler endlos), bleiben
        // aber vorgemerkt fuer den naechsten Anstoss.
        const gescheitert = new Set<Eintrag>();
        let letzterFehler: unknown = null;
        try {
            for (;;) {
                const runde = Array.from(ausstehend.values()).filter(a => !gescheitert.has(a));
                if (!runde.length) break;
                for (const eintrag of runde) {
                    const { phaseId } = eintrag.auftrag;
                    if (ausstehend.get(phaseId) === eintrag) ausstehend.delete(phaseId);
                    opt.melde('speichert');
                    unterwegs = eintrag;
                    try {
                        await opt.schreibe(eintrag.auftrag);
                        unterwegs = null;
                        // Der geschriebene Stand enthaelt alles bis zu seiner
                        // Version — genau diese Wartenden sind bedient, nicht
                        // die auf einen juengeren Stand.
                        bestaetigtBis.set(phaseId, Math.max(bestaetigtBis.get(phaseId) || 0, eintrag.version));
                        warterBis(phaseId, eintrag.version).forEach(w => w.erfuellen());
                    } catch (e) {
                        unterwegs = null;
                        gescheitert.add(eintrag);
                        letzterFehler = e;
                        // Nur zuruecklegen, wenn fuer DIESE Phase inzwischen
                        // nichts Juengeres kam — das Juengere enthaelt die
                        // Aenderung ohnehin. Dann warten auch die Zusagen auf
                        // den juengeren Stand weiter; sonst erfahren sie den
                        // Fehler jetzt.
                        if (!ausstehend.has(phaseId)) {
                            ausstehend.set(phaseId, eintrag);
                            warterBis(phaseId, eintrag.version).forEach(w => w.ablehnen(e));
                        }
                    }
                }
            }
            // Was jetzt noch aussteht, ist in diesem Durchgang gescheitert.
            if (ausstehend.size) opt.melde('fehler', opt.fehlerText(letzterFehler));
            else opt.melde('gespeichert', null);
        } finally {
            laeuft = false;
        }
    };

    const vormerken = (auftrag: SpeicherAuftrag, verzoegerungMs: number): number => {
        const version = (letzteVersion.get(auftrag.phaseId) || 0) + 1;
        letzteVersion.set(auftrag.phaseId, version);
        ausstehend.set(auftrag.phaseId, { auftrag, version });
        opt.melde('wartet');
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            void speichern();
        }, Math.max(0, verzoegerungMs || 0));
        return version;
    };

    const abschliessen = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        if (ausstehend.size) void speichern();
    };

    const ausstehendFuer = (phaseId: string): PlanungUnterlage[] | null => {
        const vorgemerkt = ausstehend.get(phaseId);
        if (vorgemerkt) return vorgemerkt.auftrag.liste;
        return unterwegs && unterwegs.auftrag.phaseId === phaseId ? unterwegs.auftrag.liste : null;
    };

    const aktuelleVersion = (phaseId: string): number => letzteVersion.get(phaseId) || 0;

    const bestaetigung = (phaseId: string, version: number): Promise<void> =>
        new Promise<void>((erfuellen, ablehnen) => {
            if ((bestaetigtBis.get(phaseId) || 0) >= version) { erfuellen(); return; }
            const liste = warter.get(phaseId) || [];
            liste.push({ version, erfuellen, ablehnen });
            warter.set(phaseId, liste);
        });

    return { vormerken, speichern, abschliessen, ausstehendFuer, aktuelleVersion, bestaetigung };
};
